import { useState } from "react";
import { Play } from "lucide-react";
import { FaFacebook } from "react-icons/fa";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { embedUrl, youtubeThumb } from "@/lib/media";
import { fileUrl } from "@/lib/api";
import { useVideoUrl } from "@/lib/useVideoUrl";

// Picture on each video tile: your own uploaded thumbnail first, then YouTube's. Facebook has no public thumbnail,
// so its tile shows Facebook's own small player (not clickable; the tile opens the big popup).
const Thumb = ({ v }) => {
  const url = useVideoUrl(v.url);
  const thumb = v.thumb ? fileUrl(v.thumb) : youtubeThumb(v.url);
  if (thumb) return <img src={thumb} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />;
  return (
    <span className="absolute inset-0 overflow-hidden" style={{ backgroundColor: "#0B111E" }}>
      {url && <iframe src={embedUrl(url)} title="" aria-hidden="true" tabIndex={-1} loading="lazy" scrolling="no" className="pointer-events-none absolute inset-0 h-full w-full border-0" />}
      <FaFacebook className="absolute left-2 top-2 h-5 w-5" style={{ color: "#FFFFFF" }} />
    </span>
  );
};

export const COLS = { 4: "md:grid-cols-3 lg:grid-cols-4 lg:gap-x-6", 6: "md:grid-cols-3 lg:grid-cols-6 lg:gap-x-5", 8: "md:grid-cols-4 lg:grid-cols-8 lg:gap-x-4" };

// Grid of YouTube/Facebook videos: 2 columns on phones, and 4, 6 or 8 on desktop (chosen in the This page tab). A click opens a large popup player.
const Player = ({ v }) => {
  const url = useVideoUrl(v.url);
  if (!url) return <div className="aspect-video max-h-[80vh] w-full" />;
  return <iframe src={`${embedUrl(url)}${url.includes("facebook") ? "&autoplay=true" : ""}`} title={v.title || "Video"} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen className="aspect-video max-h-[80vh] w-full" />;
};

export const VideoGallery = ({ id, items, cols = 8 }) => {
  const [open, setOpen] = useState(null);
  const list = (Array.isArray(items) ? items : []).filter((v) => embedUrl(v?.url));
  if (!list.length) return null;
  return (
    <>
      <ul data-testid={`video-gallery-${id}`} data-cols={COLS[cols] ? cols : 8} className={`grid grid-cols-2 gap-x-4 gap-y-7 ${COLS[cols] || COLS[8]}`}>
        {list.map((v, i) => (
          <li key={v.id || i}>
            <button type="button" data-testid={`video-gallery-${id}-item-${i}`} onClick={() => setOpen(v)} aria-label={`Play ${v.title || "video"}`} className="group block w-full text-left">
              <span className="relative block aspect-video w-full overflow-hidden rounded-2xl bg-[#131C2E]">
                <Thumb v={v} />
                <span className="absolute inset-0 bg-black/15 transition-colors duration-300 group-hover:bg-black/30" />
                <span className="absolute left-1/2 top-1/2 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full shadow-lg transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: "#D4AF37", color: "#0B111E" }}>
                  <Play className="h-5 w-5 translate-x-[1px] fill-current" />
                </span>
              </span>
              {v.title && <span data-testid={`video-gallery-${id}-title-${i}`} className="mt-2.5 line-clamp-2 block font-display text-base font-semibold leading-snug text-[#F4EEE2]">{v.title}</span>}
              {v.pastor && <span data-testid={`video-gallery-${id}-pastor-${i}`} className="mt-0.5 block font-ui text-xs text-[#B7BFCC]">{v.pastor}</span>}
            </button>
          </li>
        ))}
      </ul>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid={`video-gallery-${id}-lightbox`} className="w-[94vw] max-w-[1400px] border-0 bg-black p-0 sm:max-w-[1400px]">
          <DialogTitle className="sr-only">{open?.title || "Video"}</DialogTitle>
          {open && (
            <div>
              <Player v={open} />
              {(open.title || open.pastor) && (
                <p className="px-5 py-3 font-ui text-sm" style={{ color: "#FFFFFF" }}>
                  <b>{open.title}</b>{open.pastor ? <span style={{ color: "#D1D5DB" }}> · {open.pastor}</span> : null}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
